<template>
    <div class="pl-page pl-page--ambient">
        <!-- The roster step used to carry a full-width topbar under the app
             menu — a second band of chrome before any content. It is the GM
             dock arrangement now: a fixed identity island and a tools island,
             neither of which costs layout height. The campaign step has no dock
             at all; its own centred title is the page. -->
        <PageDock
            v-if="stage === 'roster' && dashboardState === 'ready'"
            :title="activeCampaignName || t('player.lobby.title')"
            :eyebrow="t('player.lobby.campaignEyebrow')"
            :back-label="canLeaveCampaign ? t('player.lobby.backToCampaigns') : ''"
            @back="backToCampaigns"
        >
            <template #tools>
                <label class="pl-search">
                    <i class="fas fa-magnifying-glass" aria-hidden="true"></i>
                    <input
                        ref="searchBox"
                        v-model="query"
                        type="search"
                        class="pl-input"
                        :placeholder="t('player.lobby.searchPlaceholder')"
                        :aria-label="t('player.lobby.searchLabel')"
                        autocomplete="off"
                    />
                    <kbd v-if="!query">/</kbd>
                </label>
                <button
                    type="button"
                    class="pl-btn pl-btn--ghost pl-btn--sm"
                    :aria-label="t('player.common.refresh')"
                    @click="load()"
                >
                    <i class="fas fa-rotate" :class="{ 'pl-spinner': dashboardState === 'loading' }"
                       aria-hidden="true"></i>
                </button>
            </template>
        </PageDock>

        <h1 v-if="stage === 'roster'" ref="pageTitle" tabindex="-1" class="pl-visually-hidden">
            {{ activeCampaignName || t('player.lobby.title') }}
        </h1>

        <p v-if="persistFailed" class="pl-banner pl-banner--danger" role="alert">
            {{ t('player.lobby.campaignPersistFailed') }}
            <button type="button" class="pl-btn pl-btn--ghost pl-btn--sm" @click="retryPersist">
                {{ t('player.common.retry') }}
            </button>
        </p>

        <div v-if="dashboardState === 'no-campaigns'" class="pl-page__body">
            <div class="pl-empty">
                <i class="fas fa-flag" aria-hidden="true"></i>
                <p class="pl-empty__title">{{ t('player.lobby.noCampaignsTitle') }}</p>
                <p class="pl-empty__hint">{{ t('player.lobby.noCampaignsHint') }}</p>
                <div class="pl-topbar__actions">
                    <button type="button" class="pl-btn pl-btn--primary" @click="openDiscord">
                        {{ t('player.lobby.joinDiscord') }}
                    </button>
                    <router-link class="pl-btn pl-btn--ghost" to="/faq">
                        {{ t('player.lobby.readFaq') }}
                    </router-link>
                </div>
            </div>
        </div>

        <div v-else-if="dashboardState === 'error'" class="pl-page__body">
            <div class="pl-empty" role="alert">
                <p class="pl-empty__title">{{ t('player.lobby.loadErrorTitle') }}</p>
                <p class="pl-empty__hint">{{ t('player.lobby.loadErrorHint') }}</p>
                <button type="button" class="pl-btn pl-btn--ghost" @click="load()">
                    {{ t('player.common.retry') }}
                </button>
            </div>
        </div>

        <CampaignShowcase
            v-else-if="stage === 'campaign'"
            ref="showcase"
            :campaigns="sortedCampaigns"
            :active-id="activeCampaignId"
            :loading="dashboardState === 'loading'"
            @open="openCampaign"
            @refresh="load()"
        />

        <div v-else-if="charactersState !== 'loading' && !campaignRoster.length" class="pl-page__body">
            <div class="pl-empty">
                <i class="fas fa-user-plus" aria-hidden="true"></i>
                <p class="pl-empty__title">{{ t('player.lobby.emptyCampaignTitle') }}</p>
                <p class="pl-empty__hint">{{ t('player.lobby.emptyCampaignHint') }}</p>
                <button type="button" class="pl-btn pl-btn--primary" @click="createCharacter">
                    {{ t('player.lobby.addCharacter') }}
                </button>
            </div>
        </div>

        <div v-else-if="charactersState !== 'loading' && !filtered.length" class="pl-page__body">
            <div class="pl-empty">
                <p class="pl-empty__title">{{ t('player.lobby.noMatchTitle') }}</p>
                <p class="pl-empty__hint">
                    {{ query ? t('player.lobby.noMatchSearch', { query }) : t('player.lobby.noMatchFilter') }}
                </p>
                <button type="button" class="pl-btn pl-btn--ghost" @click="clearFilters">
                    {{ t('player.common.clearFilters') }}
                </button>
            </div>
        </div>

        <CharacterShowcase
            v-else
            :campaign="activeCampaign"
            :characters="filtered"
            :selected-id="focused?.id ?? null"
            :entering="enteringId === focused?.id"
            :training="trainingId === focused?.id"
            :ending-training="endingTrainingId === focused?.id"
            :training-active="hasTraining(focused?.id)"
            :loading="charactersState === 'loading'"
            @select="focusCharacter"
            @enter="enterGame"
            @train="startTraining"
            @end-training="openEndTrainingConfirm"
            @create="createCharacter"
        />

        <ConfirmModal
            :is-open="endTrainingConfirmOpen"
            :title="t('fightHud.endTrainingConfirmTitle')"
            :message="t('fightHud.endTrainingConfirmMessage')"
            :confirm-label="t('fightHud.endTrainingConfirmYes')"
            :cancel-label="t('fightHud.endTrainingConfirmNo')"
            @confirm="confirmEndTraining"
            @cancel="cancelEndTraining"
        />

        <ConfirmModal
            :is-open="trainingErrorOpen"
            mode="alert"
            :title="t('fightHud.startTrainingErrorTitle')"
            :message="trainingErrorMessage"
            :confirm-label="t('player.common.close')"
            @confirm="closeTrainingError"
        />
    </div>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import ClientManagerService from '@/services/clientManagerService.js';
import {endTraining, startTraining as openTraining, trainingSessionFor} from '@/services/trainingService.js';
import CampaignShowcase from '@/components/Player/CampaignShowcase.vue';
import CharacterShowcase from '@/components/Player/CharacterShowcase.vue';
import PageDock from '@/components/Player/PageDock.vue';
import ConfirmModal from '@/components/Modal/ConfirmModal.vue';
import {searchTextOf} from '@/components/Player/characterPresentation.js';
import {startTrainingErrorKey} from '@/components/Player/trainingErrors.js';
import {TRAINING_ENABLED} from '@/config/features.js';

const DISCORD_URL = 'https://discord.gg/vC3TvVzK';
const CAMPAIGN_KEY = 'selectedCampaignId';

const {t, locale} = useI18n();
const router = useRouter();

const dashboardState = ref('loading');
const charactersState = ref('loading');
const stage = ref('campaign');
const clientInfo = ref(null);
const campaigns = ref([]);
const activeCampaignId = ref(null);
const persistFailed = ref(false);
const enteringId = ref(null);
const trainingId = ref(null);
const endingTrainingId = ref(null);
/* Character id -> open session, filled for the character in the dossier only:
   a session is read per character, and only one is ever on screen. */
const trainingSessions = ref({});
const endTrainingConfirmOpen = ref(false);
const pendingEndTrainingCharacterId = ref(null);
const trainingErrorOpen = ref(false);
const trainingErrorMessage = ref('');

const query = ref('');
const focusedId = ref(null);

const pageTitle = ref(null);
const showcase = ref(null);
const searchBox = ref(null);

function writeStored(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        /* storage unavailable; the choice simply does not persist */
    }
}

const ownedCharacters = computed(() => clientInfo.value?.owned_characters ?? []);

function hasTraining(characterId) {
    return !!trainingSessions.value[characterId];
}

/* The dossier is the only place a training is offered, so the session is read
   for the character it shows and cached against a second look. */
async function loadTrainingFor(characterId) {
    if (!TRAINING_ENABLED) return;
    if (!characterId || characterId in trainingSessions.value) return;
    let session = null;
    try {
        session = await trainingSessionFor(characterId);
    } catch {
        session = null;
    }
    trainingSessions.value = {...trainingSessions.value, [characterId]: session};
}

const showRoster = computed(
    () => dashboardState.value === 'ready' && stage.value === 'roster',
);

/* A player in exactly one campaign has no choice to go back to. */
const canLeaveCampaign = computed(
    () => stage.value === 'roster' && campaigns.value.length > 1,
);

const sortedCampaigns = computed(() => [...campaigns.value].sort((a, b) => {
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    if (a.is_completed !== b.is_completed) return a.is_completed ? 1 : -1;
    return String(a.name).localeCompare(String(b.name), locale.value);
}));

const activeCampaign = computed(
    () => campaigns.value.find((c) => c.id === activeCampaignId.value) ?? null,
);

const activeCampaignName = computed(() => activeCampaign.value?.name ?? '');

const campaignRoster = computed(() => ownedCharacters.value.filter(
    (character) => character.campaign && character.campaign.id === activeCampaignId.value,
));

/* The seats are in rank order, strongest first. The sort control that used to
   offer four orderings is gone with the grid: a line of ten portraits does not
   need to be re-sorted, and rank is the ordering a player already thinks in. */
function byRank(a, b) {
    return (b.rank?.grade ?? 0) - (a.rank?.grade ?? 0)
        || String(a.name ?? '').localeCompare(String(b.name ?? ''), locale.value);
}

const filtered = computed(() => {
    const needle = query.value.trim().toLowerCase();
    const result = needle
        ? campaignRoster.value.filter((character) => searchTextOf(character).includes(needle))
        : [...campaignRoster.value];
    return result.sort(byRank);
});

/* The character in the dossier. Derived rather than stored, so a roster that
   reloads, a search that excludes the current pick, or a campaign switch can
   never leave the dossier pointing at somebody who is no longer on screen. */
const focused = computed(() => filtered.value.find((c) => c.id === focusedId.value)
    ?? filtered.value[0]
    ?? null);

watch(focused, (character) => loadTrainingFor(character?.id), {immediate: true});

function hydrateCampaigns(info) {
    const merged = [...(info.play_campaigns ?? []), ...(info.master_campaigns ?? [])];
    const unique = [...new Map(merged.map((campaign) => [campaign.id, campaign])).values()];
    const counts = new Map();
    for (const character of info.owned_characters ?? []) {
        const id = character.campaign?.id;
        if (!id) continue;
        counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    campaigns.value = unique.map((campaign) => ({
        ...campaign,
        is_active: campaign.is_active !== undefined ? campaign.is_active : true,
        is_completed: campaign.is_completed !== undefined ? campaign.is_completed : false,
        characterCount: counts.get(campaign.id) ?? 0,
    }));
}

async function load() {
    dashboardState.value = 'loading';
    charactersState.value = 'loading';
    persistFailed.value = false;
    ClientManagerService.clearClientInfoCache();

    let info;
    try {
        info = await ClientManagerService.getCurrentClientInfo();
    } catch {
        dashboardState.value = 'error';
        charactersState.value = 'error';
        return;
    }

    clientInfo.value = info;
    trainingSessions.value = {};
    hydrateCampaigns(info);

    if (!campaigns.value.length) {
        dashboardState.value = 'no-campaigns';
        charactersState.value = 'ready';
        return;
    }

    const stored = readCampaignPreference();
    const target = (stored && campaigns.value.some((c) => c.id === stored) && stored)
        || (info.current_campaign
            && campaigns.value.some((c) => c.id === info.current_campaign.id)
            && info.current_campaign.id)
        || campaigns.value[0].id;

    dashboardState.value = 'ready';
    selectCampaign(target);
    /* One campaign is not a choice — land the player on their roster. With
       more than one, the stage is the door: it is where the campaign is
       picked, and `target` only decides which card starts highlighted. */
    stage.value = campaigns.value.length > 1 ? 'campaign' : 'roster';
}

function openCampaign(campaignId) {
    selectCampaign(campaignId);
    stage.value = 'roster';
    focusTitle();
}

function backToCampaigns() {
    stage.value = 'campaign';
    clearFilters();
    focusTitle();
}

/* Each step is a new page as far as the player is concerned, but the route
   never changes, so nothing moves focus on its own. Each step's <h1> takes it,
   which is also what the route announcer in App.vue would have done. */
async function focusTitle() {
    await nextTick();
    if (stage.value === 'campaign') showcase.value?.focus();
    else pageTitle.value?.focus();
}

function readCampaignPreference() {
    try {
        return localStorage.getItem(CAMPAIGN_KEY);
    } catch {
        return null;
    }
}

function selectCampaign(campaignId) {
    activeCampaignId.value = campaignId;
    focusedId.value = null;
    query.value = '';
    writeStored(CAMPAIGN_KEY, campaignId);
    persistFailed.value = false;
    charactersState.value = 'ready';
    persistCampaign(campaignId);
}

function persistCampaign(campaignId) {
    ClientManagerService.setCurrentCampaign(campaignId).catch(() => {
        persistFailed.value = true;
    });
}

function retryPersist() {
    persistFailed.value = false;
    if (activeCampaignId.value) persistCampaign(activeCampaignId.value);
}

function clearFilters() {
    query.value = '';
}

function focusCharacter(characterId) {
    focusedId.value = characterId;
}

async function enterGame(characterId) {
    if (enteringId.value) return;
    enteringId.value = characterId;
    try {
        await ClientManagerService.useCharacter(characterId);
        await router.push({name: 'Game'});
    } catch {
        enteringId.value = null;
    }
}

/* Training is its own door from the lobby, and its own route: a private duel
   against an inert dummy, fought by a full clone. The real character is never
   bound to it, so `/game` keeps playing that character in the world the whole
   time — which is why this lands on `/game/training` and never on `/game`. */
async function startTraining(characterId) {
    if (trainingId.value || endingTrainingId.value) return;
    trainingId.value = characterId;
    try {
        await openTraining(characterId);
        await router.push({name: 'GameTraining', query: {character: characterId}});
    } catch (error) {
        trainingId.value = null;
        trainingErrorMessage.value = t(startTrainingErrorKey(error));
        trainingErrorOpen.value = true;
    }
}

function closeTrainingError() {
    trainingErrorOpen.value = false;
    trainingErrorMessage.value = '';
}

function openEndTrainingConfirm(characterId) {
    pendingEndTrainingCharacterId.value = characterId;
    endTrainingConfirmOpen.value = true;
}

function cancelEndTraining() {
    endTrainingConfirmOpen.value = false;
    pendingEndTrainingCharacterId.value = null;
}

/* Ending deletes the clone, the dummy and the session outright — the next
   start builds a fresh clone at full HP/MP/AP. */
async function confirmEndTraining() {
    endTrainingConfirmOpen.value = false;
    const characterId = pendingEndTrainingCharacterId.value;
    pendingEndTrainingCharacterId.value = null;
    if (!characterId || endingTrainingId.value) return;
    endingTrainingId.value = characterId;
    try {
        await endTraining(characterId);
        trainingSessions.value = {...trainingSessions.value, [characterId]: null};
    } finally {
        endingTrainingId.value = null;
    }
}

function createCharacter() {
    router.push({name: 'CharacterSubmit'});
}

function openDiscord() {
    window.open(DISCORD_URL, '_blank', 'noopener');
}

function onKeydown(event) {
    if (!showRoster.value) return;
    const target = event.target;
    const typing = target instanceof HTMLElement
        && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

    if (event.key === '/' && !typing) {
        event.preventDefault();
        searchBox.value?.focus();
        return;
    }
    if (event.key === 'Escape' && target === searchBox.value) {
        if (query.value) query.value = '';
        else searchBox.value?.blur();
    }
}

onMounted(async () => {
    window.addEventListener('keydown', onKeydown);
    await load();
    await focusTitle();
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
</style>
